import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  selectOrderDetailsVm,
  selectGetOrderDetailsRequest,
  selectSendSalesSupportRequest
} from "../reducers/orderDetailsViewReducer";
import { orderSagas } from "../sagas/orderSagas";
import { AppContentWrapper } from "../../../../framework/components/AppContentWrapper";
import { getOrderViewcolumnDefs } from "./OrdersView";
import styled from "styled-components";
import { OrderInfo, getCompanyCol } from "../components/OrderInfo";
import { RequestStatus } from "../../../../framework/state/requestStatus";
import { LoadingIndicator } from "@abb/abb-common-ux-react";
import ContactSalesSupportDialog, {
  ContactSalesSupportFields
} from "../components/ContactSalesSupportDialog";
import { ApiOrderSalesSupportRequestPostRequest, AccessRights } from "../../../../api";
import { OrderDetailsGrid } from "../components/OrderDetailsGrid";
import { ShippingDetailsGrid } from "../components/ShippingDetailsGrid";
import { routes } from "../../../../utilities/routes";
import { getUserAccessRights } from "../../../common/reducers/userReducer";
import { isAuthorizedToComponent } from "../../../../utilities/authUtils";
import { GraphicalStatus } from "../components/GraphicalStatus";
import { orderStatusLookup } from "../../../../constants/orderStatusConstant";
import { OrderCancelledInfo } from "../components/OrderCancelledInfo";
import { Button } from "framework/components/Button";
import { useTranslation } from "react-i18next";
import { ShippingDetailDocumentTypes } from "../../../../api/models/ShippingDetailDocumentTypes";

const ContactSalesButton = styled(Button)`
  margin-top: ${(props) => props.theme.sizes.sm};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const StatusContainer = styled.div`
  height: 100px;
  width: 100%;
  margin-top: ${(props) => props.theme.sizes.l};
  margin-bottom: ${(props) => props.theme.sizes.l};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const getStatusComponent = (status: string | null | undefined): React.ReactNode => {
  if (status) {
    return (
      <StatusContainer>
        {status === "Cancelled" ? (
          <OrderCancelledInfo />
        ) : (
          <GraphicalStatus statusNumber={orderStatusLookup[status]} />
        )}
      </StatusContainer>
    );
  }
  return <StatusContainer />;
};

const GridDiv = styled.div<{ noMargin?: boolean }>`
  margin-top: ${(props) => (props.noMargin ? "0px" : props.theme.sizes.xl)};
  width: 100%;
`;

// Hack min-heigh to 0 so that extra space is not generated under the grid.
// Should come up with a better solution. Problem is mainly present when there is only one row in the grid.
const OrderDetailsContainer = styled.div`
  width: 100%;
  .ag-center-cols-container {
    min-height: 0px !important;
  }
  .ag-layout-auto-height .ag-center-cols-clipper {
    min-height: 0px !important;
  }
`;

const LoadingIndicatorContainer = styled.div`
  margin: auto;
`;

export interface ProductInformationLinkParameters {
  orderAcknowledgementNumber: string;
  orderLineItem?: string;
}

const OrderDetailsView = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  // State
  const [contactSalesSupportDialogOpen, setContactSalesSupportDialogOpen] = useState(false);
  // Params
  const { orderIdentifier } = useParams<{ orderIdentifier?: string }>();
  // Selectors
  const orderDetailsVm = useSelector(selectOrderDetailsVm);
  const getOrderDetailsRequest = useSelector(selectGetOrderDetailsRequest);
  const sendSalesSupportRequest = useSelector(selectSendSalesSupportRequest);
  const userAccessRights = useSelector(getUserAccessRights);

  const { t } = useTranslation();

  const getContactSalesSupportReasons = () => {
    return orderDetailsVm?.contactSalesSupportReasons ?? [];
  };

  useEffect(() => {
    if (orderIdentifier) {
      dispatch(
        orderSagas.getOrderdetails.createAction({
          orderIdentifier: orderIdentifier
        })
      );
    }
  }, [orderIdentifier, dispatch]);

  // Helper methods
  const handleSenSalesSupportRequest = (fields: ContactSalesSupportFields) => {
    const sendSalesSupportRequestParams: ApiOrderSalesSupportRequestPostRequest = {
      contactSalesSupportCommand: {
        orderAcknowledgementNumber: orderDetailsVm?.order.orderAcknowledgementNumber,
        message: fields.description,
        reason: fields.reasonId
      }
    };
    dispatch(orderSagas.sendSalesSupport.createAction(sendSalesSupportRequestParams));
  };

  const hasShippingDetails: boolean =
    orderDetailsVm?.shippingDetails !== undefined && orderDetailsVm.shippingDetails.length > 0;

  const goToProductInformation = (params: ProductInformationLinkParameters) => {
    history.push({
      pathname: routes.deliveries.productInformation.index,
      state: {
        productInformationLinkParameters: { ...params }
      }
    });
  };

  const hasOrderTrackingRights = isAuthorizedToComponent(userAccessRights, {
    operation: "any",
    identifier: orderDetailsVm?.order.companyId,
    accessRights: [AccessRights.AdvancedOrderTracking]
  });

  const hasProductInformationAccessRights = isAuthorizedToComponent(userAccessRights, {
    operation: "any",
    identifier: undefined,
    accessRights: [AccessRights.DownloadTestCertificates]
  });

  const hasTestCertificates =
    orderDetailsVm?.orderLines.some((ol) => ol.hasTestCertificates) ?? false;

  const hasSoftwareDocuments =
    orderDetailsVm?.orderLines.some((ol) => ol.hasSoftwareDocuments) ?? false;

  const handleOrderItemFilesDownload = (
    shippingNumber: string,
    orderAcknowledgementNumber: string,
    documentTypes: ShippingDetailDocumentTypes[]
  ) => {
    dispatch(
      orderSagas.downloadShippingDetailFiles.createAction({
        shippingNumber: shippingNumber,
        orderAcknowledgementNumber: orderAcknowledgementNumber,
        documentTypes: documentTypes
      })
    );
  };

  if (getOrderDetailsRequest.status === RequestStatus.Failed) {
    return <div>Loading failed</div>;
  }

  const orderViewcolumnDefs = getOrderViewcolumnDefs(t, hasOrderTrackingRights);

  return (
    <AppContentWrapper pageTitle={t("Order")}>
      {getOrderDetailsRequest.status === RequestStatus.Pending ? (
        <LoadingIndicatorContainer>
          <LoadingIndicator type="radial" sizeClass="large" determinate={false} />
        </LoadingIndicatorContainer>
      ) : (
        <OrderDetailsContainer>
          <ContactSalesSupportDialog
            isOpen={contactSalesSupportDialogOpen}
            onClose={() => setContactSalesSupportDialogOpen(false)}
            reasons={getContactSalesSupportReasons()}
            onSendSupportRequest={handleSenSalesSupportRequest}
            sendSupportRequest={sendSalesSupportRequest}
          />
          <GridDiv noMargin>
            <OrderInfo
              columnDefs={
                orderDetailsVm?.company
                  ? [getCompanyCol(t), ...orderViewcolumnDefs]
                  : orderViewcolumnDefs
              }
              order={orderDetailsVm?.order}
              company={orderDetailsVm?.company}
              hasProductInformationAccessRight={hasProductInformationAccessRights}
              hasTestCertificates={hasTestCertificates}
              hasSoftwareDocuments={hasSoftwareDocuments}
              goToProductInformation={goToProductInformation}
              hasOrderTrackingRights={hasOrderTrackingRights}
            />
            {hasOrderTrackingRights ? (
              <ButtonContainer>
                <ContactSalesButton
                  sizeClass="medium"
                  buttonType="secondary"
                  text={t("Contact order handling")}
                  onClick={() => setContactSalesSupportDialogOpen(true)}
                />
              </ButtonContainer>
            ) : null}
            {getStatusComponent(orderDetailsVm?.order.status)}
          </GridDiv>
          <GridDiv noMargin={hasOrderTrackingRights}>
            <OrderDetailsGrid
              rowData={orderDetailsVm?.orderLines}
              goToProductInformation={goToProductInformation}
              showProductInformationLink={hasSoftwareDocuments || hasProductInformationAccessRights}
              hasOrderTrackingRights={hasOrderTrackingRights}
            />
          </GridDiv>
          {hasShippingDetails ? (
            <GridDiv>
              <ShippingDetailsGrid
                rowData={orderDetailsVm?.shippingDetails}
                hasOrderTrackingRights={hasOrderTrackingRights}
                downloadShippingDetailFile={handleOrderItemFilesDownload}
              />
            </GridDiv>
          ) : null}
        </OrderDetailsContainer>
      )}
    </AppContentWrapper>
  );
};

export default OrderDetailsView;
